import type { BagRefusalReason, BagSortOrder, Rules } from "odyssey-shared";
import { movable, sortChanges, splitRefusalOf, splitTarget } from "./movable.ts";
import { EMPTY_SLOT, NO_BAG, PAGE_TIMER, PENDING_TIMEOUT_MS, changedSlots, held, keptLeft, sortEnded, sortSent, type BagSlot, type BagTimer, type DestroySender, type DropSender, type KeepSender, type MoveSender, type SortSender, type SplitSender, type UseSender, type Waiting } from "./snapshot.ts";
import { BagListeners } from "./source.ts";
import { BagWaits, SORT_WAIT } from "./waits.ts";
import type { BagEvent } from "./wire.ts";

export { PENDING_TIMEOUT_MS, type BagRefusal, type BagSlot, type BagSnapshot, type BagSortState, type BagSource, type BagTimer, type DestroySender, type DropSender, type KeepSender, type MoveSender, type SortSender, type SplitSender, type UseSender } from "./snapshot.ts";

/**
 * The page's bag (P2.8-T11, ADR-091 decisions 8, 13 and 21), owned by the game rather than by React, as the HUD's
 * store is. It holds only what the server said: `bag` replaces it and names its size, `bagChanged` rewrites the slots
 * it names, and nothing the player does changes an item or a count before the server commits it (CONTRACTS 1.3,
 * Category C). A use marks its slot pending from the request until `bagChanged` for a use or `itemRefused` names it,
 * or until `PENDING_TIMEOUT_MS` has passed with no answer, so a request lost on the way never leaves a slot hatched
 * and unusable for good.
 * The window's open state lives here too, so the key, the close button and the router's layer always agree.
 * Listeners are told only on a change, and the snapshot is replaced on each change and kept otherwise, as
 * `useSyncExternalStore` needs.
 *
 * Rearranging (P2.8-T14, ADR-091 decision 21 and its note of that row): a slot is picked up (`pick`, the design's
 * `selected`) and placed on another (`place`), which sends one `bagMove` and marks both slots pending until a `move`
 * change names them, an `itemRefused` for the move names the slot it came from, a `bag` replaces the bag, or
 * `PENDING_TIMEOUT_MS` passes. Before it sends, the page reads the move by the bag's own rule (`moveInBag`) against what
 * it holds: a slot placed on itself, a merge onto a stack with no room and a slot pending are put back with nothing
 * sent, since the server would refuse them. Nothing moves on the page before the server commits. The selection is
 * the store's alone, so the two clicks, the drag and the keyboard all pick up and put back the same way, and a later
 * surface (the bar, P2.8-T29) can take a slot picked up here.
 *
 * The author's controls of 2026-10-08 (ADR-091's note of them): `split` sends one `bagSplit` for a stack the bag's rule would halve and
 * hatches the slot and the slot its new half lands in; `destroy` sends one `destroyItem` naming the item the page holds
 * there and hatches the slot. Each waits for its answer as a move does, and a split the rule refuses (a stack of 1, a
 * full bag) is refused quietly, nothing sent. A slot picked up that turns pending is put back at once.
 * A move waiting `PENDING_SPINNER_DELAY_MS` marks the snapshot `slow` while it is the latest (`waits.ts`). A sort
 * (P2.8-T48, ADR-125 decision 3.6) hatches every slot until its answer, a `bag` or `PENDING_TIMEOUT_MS`. Keep in place
 * (P2.8-T49, decision 6) sends one `bagKeep`, hatching nothing; the lock shows when `bagKept` names the slot.
 *
 * The potion on the bar (P2.8-T12) reads the snapshot's slots for the first consumable and its total, calls `use`, and
 * hears each refusal through `onRefused`.
 *
 * @example
 * ```ts
 * const bag = new BagStore(PAGE_RULES);
 * bag.bindUse((slot, item) => connection.sendUseItem(slot, item));
 * bag.receive({ kind: "bag", size: 20, items: [[0, "minor_healing_potion", 5]] });
 * bag.use(0);
 * ```
 */
export class BagStore extends BagListeners {
  private entry = false;
  private sender: UseSender | null = null;
  private mover: MoveSender | null = null;
  private splitter: SplitSender | null = null;
  private destroyer: DestroySender | null = null;
  private sorter: SortSender | null = null;
  private keeper: KeepSender | null = null;
  private dropper: DropSender | null = null;
  private readonly keeping = new Map<number, { readonly kept: boolean; readonly cancel: () => void }>();
  private readonly waits: BagWaits;

  /**
   * @param rules The page's rules, which say whether an item can be used.
   * @param timer Schedules a pending mark's timeout and a move's slow mark; `setTimeout` on the page.
   */
  public constructor(
    private readonly rules: Rules,
    private readonly timer: BagTimer = PAGE_TIMER,
  ) {
    super();
    this.waits = new BagWaits(timer);
  }

  /**
   * Sets what sends a `useItem`: the session's connection, which declines while it is not ready or while the server
   * does not yet take the request.
   *
   * @param sender The sender.
   */
  public bindUse(sender: UseSender): void {
    this.sender = sender;
  }

  /**
   * Sets what sends a `bagMove`: the session's connection, which declines while it is not ready, while the server does
   * not yet take the request, while the player is frozen, or past the page's budget of item requests.
   *
   * @param mover The sender.
   */
  public bindMove(mover: MoveSender): void {
    this.mover = mover;
  }

  /**
   * Sets what sends a `bagSplit`, a `destroyItem`, a `bagSort`, a `bagKeep` and a `dropItem`: the session's connection,
   * which declines as it does for a move.
   *
   * @param splitter Sends a split.
   * @param destroyer Sends a destroy.
   * @param sorter Sends a sort; none is sent while it is absent.
   * @param keeper Sends a keep; none is sent while it is absent.
   * @param dropper Sends a drop (P2.8-T34); none is sent while it is absent.
   */
  public bindEdits(splitter: SplitSender, destroyer: DestroySender, sorter: SortSender | null = null, keeper: KeepSender | null = null, dropper: DropSender | null = null): void {
    this.splitter = splitter;
    this.destroyer = destroyer;
    this.sorter = sorter;
    this.keeper = keeper;
    this.dropper = dropper;
  }

  /**
   * Takes one inventory message from the server.
   *
   * @param event The message.
   */
  public receive(event: BagEvent): void {
    if (event.kind === "bag") {
      const length = event.items.reduce((most, [slot]) => Math.max(most, slot + 1), event.size);
      const slots: BagSlot[] = Array.from({ length }, () => EMPTY_SLOT);
      for (const [slot, item, quantity] of event.items) {
        slots[slot] = { item, quantity, pending: false };
      }
      this.waits.endAll();
      this.endKeeps(() => true);
      this.change({ ...this.snapshot, size: event.size, slots, kept: [], selected: null, moving: null, slow: false, sort: { ...this.snapshot.sort, pending: null, slow: false } });
      return;
    }
    if (event.kind === "kept") {
      this.endKeeps((slot, kept) => event.slots.includes(slot) === kept);
      this.change({ ...this.snapshot, kept: event.slots });
      return;
    }
    if (event.kind === "changed") {
      if (this.snapshot.size === null) {
        console.warn("ignoring a bagChanged that came before any bag");
        return;
      }
      let slots = changedSlots(this.snapshot.slots, event.slots, event.cause !== "loot");
      this.snapshot = { ...this.snapshot, kept: keptLeft(this.snapshot.kept, slots) };
      if (event.cause === "sort") {
        this.endSort(slots, true);
        return;
      }
      for (const [slot] of event.slots) {
        if (slots[slot]?.pending !== true) {
          this.waits.end(slot);
        }
      }
      if (event.cause !== "use" && event.cause !== "loot") {
        const answered = this.waits.forget(event.slots, event.cause);
        slots = slots.map((each, index) => (each.pending && answered.includes(index) && !this.waits.inMove(index) ? { ...each, pending: false } : each));
      }
      this.change({ ...this.snapshot, slots, selected: held(slots, this.snapshot.selected), ...this.waits.marks() });
      return;
    }
    if (event.request === "use") {
      this.settle(event.slot);
    } else if (event.request === "sort") {
      this.endSort(this.snapshot.slots);
    } else if (event.request === "keep") {
      this.endKeeps((slot) => slot === event.slot);
    } else {
      this.settleMove(event.slot, event.request);
    }
    this.refused({ request: event.request, slot: event.slot, reason: event.reason });
  }

  /**
   * Asks the server to use the item in a slot (ADR-091 decision 13): sent only for a consumable of the page's rules
   * that the slot holds, with no request already waiting on it. The slot is pending from here until the server
   * answers; its count does not change until then.
   *
   * @param slot The slot.
   * @returns Whether the request was sent.
   */
  public use(slot: number): boolean {
    const held = this.snapshot.slots[slot];
    if (held === undefined || held.item === null || held.pending || this.sender === null) {
      return false;
    }
    if (this.rules.items.get(held.item)?.kind !== "consumable" || !this.sender(slot, held.item)) {
      return false;
    }
    this.change({ ...this.snapshot, selected: this.snapshot.selected === slot ? null : this.snapshot.selected, slots: this.snapshot.slots.map((each, index) => (index === slot ? { ...each, pending: true } : each)) });
    this.waits.end(slot);
    this.waits.timeout(slot, () => this.settle(slot));
    return true;
  }

  /**
   * Picks up a slot to move it (the design's `selected`): only a slot that holds an item the server named and waits for
   * no answer, and only while a sender is bound. Picking up the slot already picked up puts it back.
   *
   * @param slot The slot.
   * @returns Whether a slot is now picked up.
   */
  public pick(slot: number): boolean {
    const held = this.snapshot.slots[slot];
    if (this.snapshot.selected === slot) {
      this.putBack();
      return false;
    }
    if (held === undefined || held.item === null || held.pending || this.mover === null) {
      return false;
    }
    this.change({ ...this.snapshot, selected: slot });
    return true;
  }

  /**
   * Places the slot picked up onto another: moves onto an empty slot, merges onto the same item, swaps with another. The
   * page reads the move by the bag's rule first, and a move the server would refuse (onto itself, a merge with no room,
   * a slot past the bag, a slot waiting for an answer) is put back with nothing sent. A move sent marks both slots
   * pending until the server answers; nothing moves before it commits.
   *
   * @param slot The slot placed onto.
   * @returns Whether a `bagMove` was sent.
   */
  public place(slot: number): boolean {
    const from = this.snapshot.selected;
    if (from === null) {
      return false;
    }
    if (this.mover === null || !this.placeable(slot) || !this.mover(from, slot)) {
      this.putBack();
      return false;
    }
    this.wait(from, { kind: "move", other: slot });
    return true;
  }

  /**
   * Whether a slot can be split now as the page holds the bag: a stack of 2 or more with an empty slot in the bag, no
   * answer waiting on it, and a sender bound.
   *
   * @param slot The slot.
   * @returns True when `split` would send.
   */
  public canSplit(slot: number): boolean {
    const held = this.snapshot.slots[slot];
    const size = this.snapshot.size;
    return held !== undefined && !held.pending && size !== null && this.splitter !== null && splitTarget(this.rules, this.snapshot.slots, slot, size) !== null;
  }

  /**
   * Why the bag's rule would refuse a split of a slot as the page holds the bag (ADR-125 decision 7.2): `empty` for a
   * stack of 1, `full` for a bag with no empty slot, `range` past the bag or before the server has said its size;
   * null when the rule takes it, whether or not `canSplit` would also send it now.
   *
   * @param slot The slot.
   * @returns The rule's reason, or null.
   */
  public splitRefusal(slot: number): BagRefusalReason | null {
    const size = this.snapshot.size;
    return size === null ? "range" : splitRefusalOf(this.rules, this.snapshot.slots, slot, size);
  }

  /**
   * Splits a stack in half (the author's controls of 2026-10-08): the new half goes to the first empty slot, the slot
   * split keeping the larger half, once the server commits. A split the bag's rule refuses (a stack of 1, a full bag)
   * is refused quietly, nothing sent. A split sent hatches both slots until the server answers.
   *
   * @param slot The slot split.
   * @returns Whether a `bagSplit` was sent.
   */
  public split(slot: number): boolean {
    const size = this.snapshot.size;
    const target = size === null ? null : splitTarget(this.rules, this.snapshot.slots, slot, size);
    if (target === null || this.snapshot.slots[slot]?.pending !== false || this.snapshot.slots[target]?.pending === true || this.splitter === null || !this.splitter(slot)) {
      return false;
    }
    this.wait(slot, { kind: "split", other: target });
    return true;
  }

  /**
   * Destroys the whole stack of a slot (the author's controls of 2026-10-08), once the player has confirmed it: sends
   * one `destroyItem` naming the item the page holds there and the count the player confirmed, and hatches the slot
   * until the server answers. A slot that no longer holds that count sends nothing, and the server refuses a count
   * that differs from its own (loot merged onto the stack after the confirm), so no unit the player did not confirm is
   * destroyed. The stack leaves the page only when the server commits it.
   *
   * @param slot The slot.
   * @param quantity The count the player confirmed.
   * @returns Whether a `destroyItem` was sent.
   */
  public destroy(slot: number, quantity: number): boolean {
    const held = this.snapshot.slots[slot];
    if (held === undefined || held.item === null || held.pending || held.quantity !== quantity || this.destroyer === null || !this.destroyer(slot, held.item, quantity)) {
      return false;
    }
    this.wait(slot, { kind: "destroy", other: slot });
    return true;
  }

  /**
   * Drops the whole stack of a slot onto the ground at the character's feet (P2.8-T34, ADR-091's amendment for P2.8-T34),
   * from the item menu's Drop or a drag released over the game: sends one `dropItem` naming the item and the count the
   * page holds there, puts the slot back if it was picked up, and hatches it until the server answers. The server
   * refuses any other count than its own, so it never drops a unit the page did not show. The stack leaves the page only
   * when the server commits it; a quest item is never sent.
   *
   * @param slot The slot.
   * @returns Whether a `dropItem` was sent.
   */
  public drop(slot: number): boolean {
    const held = this.snapshot.slots[slot];
    if (held === undefined || held.item === null || held.pending || this.rules.items.get(held.item)?.kind === "quest" || this.dropper === null || !this.dropper(slot, held.item, held.quantity)) {
      return false;
    }
    this.wait(slot, { kind: "drop", other: slot });
    return true;
  }

  /**
   * Keeps a stack in place, or lets it move (ADR-125 decision 6.4): sends one `bagKeep` for a slot holding an item,
   * with no answer waiting on it, whose flag differs from the one asked and with no keep of its own still unanswered,
   * while a sender is bound. Nothing changes on the page until `bagKept` names the kept slots; a keep is unanswered
   * until a `bagKept` shows its flag, its refusal, a `bag`, or `PENDING_TIMEOUT_MS`.
   *
   * @param slot The slot.
   * @param kept Whether to keep it in place.
   * @returns Whether a `bagKeep` was sent.
   */
  public keep(slot: number, kept: boolean): boolean {
    const held = this.snapshot.slots[slot];
    if (held === undefined || held.item === null || held.pending || this.keeper === null || this.keeping.has(slot) || this.snapshot.kept.includes(slot) === kept || !this.keeper(slot, kept)) {
      return false;
    }
    this.keeping.set(slot, { kept, cancel: this.timer(() => this.keeping.delete(slot), PENDING_TIMEOUT_MS) });
    return true;
  }

  /**
   * Forgets the unanswered keeps an answer settles, and their timeouts.
   *
   * @param settles Whether the answer settles a keep of a slot asking a flag.
   */
  private endKeeps(settles: (slot: number, kept: boolean) => boolean): void {
    for (const [slot, waiting] of this.keeping) {
      if (settles(slot, waiting.kept)) {
        waiting.cancel();
        this.keeping.delete(slot);
      }
    }
  }

  /**
   * Sorts the bag in an order (ADR-125 decision 3.6): sends one `bagSort` unless the bag's rule says the bag is already
   * in it, a request waits on any slot or no sender is bound, then hatches every slot until the server answers.
   *
   * @param by The order.
   * @returns Whether a `bagSort` was sent.
   */
  public sort(by: BagSortOrder): boolean {
    if (this.snapshot.size === null || this.sorter === null || this.snapshot.slots.some((each) => each.pending) || !sortChanges(this.rules, this.snapshot.slots, by, this.snapshot.size, this.snapshot.kept) || !this.sorter(by)) {
      return false;
    }
    this.change(sortSent(this.snapshot, by));
    this.waits.timeout(SORT_WAIT, () => this.endSort(this.snapshot.slots));
    this.waits.slowAfter(SORT_WAIT, () => this.change({ ...this.snapshot, sort: { ...this.snapshot.sort, slow: true } }));
    return true;
  }

  /**
   * Ends the sort waiting, if one does: its timeout and slow mark stop and every slot's hatch clears.
   *
   * @param slots The slots after the answer.
   * @param answered Whether this is the sort's `bagChanged`, whose slots land even after its wait was given up.
   */
  private endSort(slots: readonly BagSlot[], answered = false): void {
    if (this.snapshot.sort.pending !== null) {
      this.waits.end(SORT_WAIT);
      this.change(sortEnded(this.snapshot, slots));
    } else if (answered) {
      this.change({ ...this.snapshot, slots, selected: held(slots, this.snapshot.selected) });
    }
  }

  /**
   * Hatches the slots a request sent waits on, puts back a slot picked up among them, and starts its timeout.
   *
   * @param from The slot the request names first.
   * @param waiting The request.
   */
  private wait(from: number, waiting: Waiting): void {
    this.waits.end(from);
    this.waits.hold(from, waiting);
    const selected = this.snapshot.selected;
    this.change({
      ...this.snapshot,
      selected: waiting.kind === "move" || selected === from || selected === waiting.other ? null : selected,
      ...this.waits.marks(),
      slots: this.snapshot.slots.map((each, index) => (index === from || index === waiting.other ? { ...each, pending: true } : each)),
    });
    this.waits.timeout(from, () => this.settleMove(from, waiting.kind));
    if (waiting.kind === "move") {
      this.waits.slowAfter(from, () => {
        const marks = this.waits.marks();
        if (marks.slow !== this.snapshot.slow) {
          this.change({ ...this.snapshot, ...marks });
        }
      });
    }
  }

  /**
   * Whether placing the slot picked up onto another would send a move: the bag's rule takes it as the page holds the
   * bag (not onto itself, not a merge with no room, not past the bag) and neither slot waits for an answer. The drag
   * reads it to draw its target's ring and its ghost's marker.
   *
   * @param slot The slot placed onto.
   * @returns True when `place` would send.
   */
  public placeable(slot: number): boolean {
    const from = this.snapshot.selected;
    const size = this.snapshot.size;
    return from !== null && from !== slot && size !== null && this.snapshot.slots[slot]?.pending !== true && this.snapshot.slots[from]?.pending !== true && movable(this.rules, this.snapshot.slots, from, slot, size);
  }

  /**
   * Puts the slot picked up back, sending nothing, as Escape, a drop outside any slot or a second click on it does.
   *
   * @returns Whether a slot was picked up.
   */
  public putBack(): boolean {
    if (this.snapshot.selected === null) {
      return false;
    }
    this.change({ ...this.snapshot, selected: null });
    return true;
  }

  /**
   * Opens or closes the window; closing it puts back a slot picked up.
   *
   * @param open Whether it is open.
   */
  public setOpen(open: boolean): void {
    if (!open) {
      this.entry = false;
    }
    if (this.snapshot.open !== open || (!open && this.snapshot.selected !== null)) {
      this.change({ ...this.snapshot, open, selected: open ? this.snapshot.selected : null });
    }
  }

  /**
   * Opens the window when it is closed, and asks it to take the keyboard's focus, as Shift+I does (ADR-061's
   * amendment "a play window never stops play"): the window is told whether or not it was open, and takes the wish
   * with `takeEntry`.
   */
  public enter(): void {
    this.entry = true;
    this.change({ ...this.snapshot, open: true });
  }

  /**
   * Whether the window was asked to take the focus since it was last asked, answered once: the window calls this as
   * it draws and focuses its close button on true.
   *
   * @returns True when an entry waits.
   */
  public takeEntry(): boolean {
    const waiting = this.entry;
    this.entry = false;
    return waiting;
  }

  /**
   * Opens the window when it is closed and closes it when it is open.
   */
  public toggle(): void {
    this.setOpen(!this.snapshot.open);
  }

  /**
   * Forgets the bag as a session ends: the next `welcome` is followed by a `bag`, and until then the page knows
   * nothing of it. The window closes with it.
   */
  public reset(): void {
    this.waits.endAll();
    this.endKeeps(() => true);
    this.entry = false;
    if (this.snapshot !== NO_BAG) {
      this.change(NO_BAG);
    }
  }

  /**
   * Clears a slot's pending mark, if it has one, and ends its wait.
   *
   * @param slot The slot.
   */
  private settle(slot: number): void {
    if (this.waits.inMove(slot)) {
      return;
    }
    this.waits.end(slot);
    if (this.snapshot.slots[slot]?.pending === true) {
      this.change({ ...this.snapshot, slots: this.snapshot.slots.map((each, index) => (index === slot ? { ...each, pending: false } : each)) });
    }
  }

  /**
   * Clears the pending marks of a move, a split or a destroy, both its slots, and ends its wait, when one of that kind
   * from that slot waits.
   *
   * @param from The slot the request named first.
   * @param kind Which request the answer is for.
   */
  private settleMove(from: number, kind: Waiting["kind"]): void {
    const waiting = this.waits.take(from, kind);
    if (waiting === undefined) {
      return;
    }
    const to = waiting.other;
    const slots = this.snapshot.slots;
    this.change({ ...this.snapshot, ...this.waits.marks(), slots: slots.map((each, index) => ((index === from || index === to) && each.pending ? { ...each, pending: false } : each)) });
  }
}
